'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { profile } from '@/config';
import { getBrandIcon } from '@/lib/socialBrandIcons';

interface SocialLink {
  id: string;
  sourceId: string;
  name: string;
  icon: string;
  href: string;
  label: string;
}

const ContactCTA: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const socialLinks: SocialLink[] = profile.socials.map((social) => ({
    id: `cta_${social.id}`,
    sourceId: social.id,
    name: social.label,
    icon: social.icon,
    href: social.url,
    label: `${social.label} Profile`,
  }));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section data-component-id="src/app/homepage/components/ContactCTA.tsx:54:4" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="54" data-component-end-line="129" data-component-file="ContactCTA.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-24%20px-6%20border-t%20border-border%20relative%20z-10%22%7D"
      ref={sectionRef}
      className="py-24 px-6 border-t border-border relative z-10"
    >
      <div data-component-id="src/app/homepage/components/ContactCTA.tsx:58:6" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="58" data-component-end-line="128" data-component-file="ContactCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container-custom%22%7D" className="container-custom">
        <div data-component-id="src/app/homepage/components/ContactCTA.tsx:59:8" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="59" data-component-end-line="127" data-component-file="ContactCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20text-center%22%7D" className="max-w-4xl mx-auto text-center">
          {/* Headline */}
          <h2 data-component-id="src/app/homepage/components/ContactCTA.tsx:61:10" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="61" data-component-end-line="69" data-component-file="ContactCTA.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22textContent%22%3A%22Let's%20Build%20Something%22%7D"
            className={`text-4xl md:text-6xl font-bold tracking-tight mb-6 transition-all duration-1000 animate-scale-in ${
              isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
            }`}
          >
            Let's Build Something
            <br data-component-id="src/app/homepage/components/ContactCTA.tsx:67:12" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="67" data-component-end-line="67" data-component-file="ContactCTA.tsx" data-component-name="br" data-component-content="%7B%22elementName%22%3A%22br%22%7D" />
            <span data-component-id="src/app/homepage/components/ContactCTA.tsx:68:12" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="68" data-component-end-line="68" data-component-file="ContactCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-gradient-animated%22%2C%22textContent%22%3A%22Together%22%7D" className="text-gradient-animated">Together</span>
          </h2>

          {/* Subtext */}
          <p data-component-id="src/app/homepage/components/ContactCTA.tsx:72:10" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="72" data-component-end-line="80" data-component-file="ContactCTA.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22Versatile%20in%20backend%20platforms%2C%20distributed%20systems%2C%20mobile%2C%20and%20frontend%20development.%5Cn%20%20%20%20%20%20%20%20%20%20%20%20Always%20excited%20to%20collaborate%20on%20meaningful%20products.%22%7D"
            className={`text-lg md:text-xl text-muted-foreground font-light mb-12 transition-all duration-1000 animate-fade-in ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDelay: '0.2s' }}
          >
            Versatile in backend platforms, distributed systems, mobile, and frontend development.
            Always excited to collaborate on meaningful products.
          </p>

          {/* Primary CTA */}
          <div data-component-id="src/app/homepage/components/ContactCTA.tsx:83:10" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="83" data-component-end-line="96" data-component-file="ContactCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            className={`mb-12 transition-[opacity,transform] duration-1000 animate-scale-in ${
              isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
            }`}
            style={{ transitionDelay: '0.3s' }}
          >
            <a data-component-id="src/app/homepage/components/ContactCTA.tsx:89:12" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="89" data-component-end-line="95" data-component-file="ContactCTA.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-3%20px-8%20py-4%20bg-primary%20hover%3Abg-primary%2F90%20text-white%20rounded-full%20font-semibold%20text-lg%20transition-all%20duration-300%20hover%3Ascale-105%20hover%3Agap-4%20glow-effect-strong%20hover%3Ashadow-2xl%22%7D"
              href={`mailto:${profile.email}`}
              className="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-primary/90 text-white rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 hover:gap-4 glow-effect-strong hover:shadow-2xl"
            >
              <Icon data-component-id="src/app/homepage/components/ContactCTA.tsx:93:14" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="93" data-component-end-line="93" data-component-file="ContactCTA.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22EnvelopeIcon%22%7D" name="EnvelopeIcon" size={24} variant="outline" />
              <span data-component-id="src/app/homepage/components/ContactCTA.tsx:94:14" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="94" data-component-end-line="94" data-component-file="ContactCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Get%20In%20Touch%22%7D">Get In Touch</span>
            </a>
          </div>

          {/* Social Links */}
          <div data-component-id="src/app/homepage/components/ContactCTA.tsx:99:10" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="99" data-component-end-line="126" data-component-file="ContactCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            className={`flex flex-wrap items-center justify-center gap-2 sm:gap-4 md:gap-6 transition-[opacity,transform] duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '0.4s' }}
          >
                {socialLinks.map((social, index) => (
              <a data-component-id="src/app/homepage/components/ContactCTA.tsx:106:14" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="106" data-component-end-line="124" data-component-file="ContactCTA.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%7D"
                key={social.id}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                    className={`flex items-center gap-2 px-3 py-2 md:px-4 glass-card rounded-full hover:scale-105 hover:glow-effect transition-transform duration-300 hover:rotate-3 stagger-${index + 1}`}
                aria-label={social.label}
              >
                {(() => {
                  const BrandIcon = getBrandIcon(social.sourceId);
                  if (BrandIcon) {
                    return <BrandIcon data-component-id="src/app/homepage/components/ContactCTA.tsx:117:27" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="117" data-component-end-line="117" data-component-file="ContactCTA.tsx" data-component-name="BrandIcon" data-component-content="%7B%22elementName%22%3A%22BrandIcon%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" aria-label={social.name} />;
                  }
                  return <Icon data-component-id="src/app/homepage/components/ContactCTA.tsx:119:25" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="119" data-component-end-line="119" data-component-file="ContactCTA.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={social.icon} size={20} variant="outline" />;
                })()}
                <span data-component-id="src/app/homepage/components/ContactCTA.tsx:121:16" data-component-path="src/app/homepage/components/ContactCTA.tsx" data-component-line="121" data-component-end-line="123" data-component-file="ContactCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20hidden%20md%3Ainline%22%7D" className="text-sm font-medium hidden md:inline">
                  {social.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactCTA;